// Desktop surface (CR #133) — a wallpaper you can treat like an OS desktop:
// widgets, icons, folders and shortcuts, with everything opening into the
// existing floating-window system (WindowManagerContext / WindowLayer).
//
// Only reachable while Desktop Mode is ON; with the flag off this route
// redirects to /dashboard so the app is exactly what it was before the trial.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Taskbar } from '@/components/desktop/Taskbar';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { FloatingRequestButton } from '@/components/dev/FloatingRequestButton';
import { APP_MENU_GROUPS } from '@/components/desktop/appMenu';
import { useAuth } from '@/lib/hooks/useAuth';
import { useWindowManager, useWindowed } from '@/lib/context/WindowManagerContext';
import { useDesktopMode, setDesktopModeEnabled } from '@/lib/hooks/useDesktopMode';
import {
  WALLPAPERS, WIDGET_CATALOG, widgetSpec, loadLayout, saveLayout, defaultLayout, uid,
  type DesktopLayout, type DesktopIcon, type DesktopWidget, type WidgetKind,
} from '@/lib/desktop/desktopLayout';
import { DesktopWidgetBody, useDesktopData, type DesktopData } from '@/components/desktop/DesktopWidgets';

const TILE = 92;          // icon tile footprint (px)
const TILE_STEP = 108;    // grid pitch used when auto-placing new icons
const GRID = 8;           // drag snap
const MIN_W = 220;
const MIN_H = 120;

const snap = (n: number) => Math.max(0, Math.round(n / GRID) * GRID);

/** First unoccupied cell scanning down a column, then across — like a real desktop. */
function nextFreeSlot(icons: DesktopIcon[], surfaceH: number): { x: number; y: number } {
  const taken = new Set(icons.filter(i => !i.parentId).map(i => `${Math.round(i.x / 4)}:${Math.round(i.y / 4)}`));
  const rows = Math.max(2, Math.floor((surfaceH - 40) / TILE_STEP));
  for (let col = 0; col < 12; col++) {
    for (let row = 0; row < rows; row++) {
      const x = 24 + col * TILE_STEP;
      const y = 24 + row * TILE_STEP;
      if (!taken.has(`${Math.round(x / 4)}:${Math.round(y / 4)}`)) return { x, y };
    }
  }
  return { x: 24, y: 24 };
}

// ── Drag plumbing ────────────────────────────────────────────────────────────

type DragKind = 'icon' | 'widget' | 'resize';
interface DragInfo {
  kind: DragKind;
  id: string;
  startX: number; startY: number;
  origX: number; origY: number; origW: number; origH: number;
  touch: boolean;
}
interface Ghost { id: string; x: number; y: number; w: number; h: number }

// ── Icon tile ────────────────────────────────────────────────────────────────

interface IconTileProps {
  icon: DesktopIcon;
  x: number; y: number;
  selected: boolean;
  light: boolean;
  dropTarget: boolean;
  onPointerDown: (e: React.PointerEvent, icon: DesktopIcon) => void;
  onOpen: (icon: DesktopIcon) => void;
  onMenu: (e: React.MouseEvent, icon: DesktopIcon) => void;
}

const IconTile = memo(({ icon, x, y, selected, light, dropTarget, onPointerDown, onOpen, onMenu }: IconTileProps) => (
  <div
    className={`absolute flex flex-col items-center gap-1 rounded-lg p-1.5 select-none touch-none cursor-pointer ${
      selected ? 'bg-white/25 ring-1 ring-white/40' : dropTarget ? 'bg-[#C77D3F]/40' : 'hover:bg-white/15'
    }`}
    style={{ left: x, top: y, width: TILE }}
    onPointerDown={e => onPointerDown(e, icon)}
    onDoubleClick={() => onOpen(icon)}
    onContextMenu={e => onMenu(e, icon)}
    title={icon.kind === 'folder' ? `${icon.label} (folder)` : icon.route}
  >
    {/* Colours are inline: icon contrast follows the WALLPAPER, and utility
        classes here would be re-mapped by the app-wide dark-theme layer. */}
    <span
      className="material-symbols-outlined flex h-12 w-12 items-center justify-center rounded-xl border text-[26px] backdrop-blur-sm"
      style={{
        fontVariationSettings: icon.kind === 'folder' ? "'FILL' 1" : undefined,
        background: light ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.16)',
        borderColor: light ? 'rgba(44,24,16,0.15)' : 'rgba(255,255,255,0.28)',
        color: light ? '#8B5A2B' : '#ffffff',
      }}
    >
      {icon.kind === 'folder' ? 'folder' : icon.icon}
    </span>
    <span
      className="w-full text-center text-[11px] font-semibold leading-tight"
      style={{
        color: light ? '#2C1810' : '#ffffff',
        textShadow: light ? undefined : '0 1px 3px rgba(0,0,0,.65)',
      }}
    >
      {icon.label}
    </span>
  </div>
));
IconTile.displayName = 'IconTile';

// ── Widget frame ─────────────────────────────────────────────────────────────

interface WidgetFrameProps {
  widget: DesktopWidget;
  x: number; y: number; w: number; h: number;
  data: DesktopData;
  onPointerDown: (e: React.PointerEvent, w: DesktopWidget, kind: 'widget' | 'resize') => void;
  onRemove: (id: string) => void;
}

const WidgetFrame = memo(({ widget, x, y, w, h, data, onPointerDown, onRemove }: WidgetFrameProps) => {
  const spec = widgetSpec(widget.kind);
  return (
    <div
      // bg-white/95 (not an arbitrary opacity) because the dark-theme layer in
      // darkTheme.css only remaps the opacity steps it knows about.
      className="absolute flex flex-col overflow-hidden rounded-xl border border-gray-300 bg-white/95 shadow-2xl backdrop-blur-xl"
      style={{ left: x, top: y, width: w, height: h }}
    >
      <div
        className="flex h-8 flex-shrink-0 cursor-grab touch-none select-none items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-2 active:cursor-grabbing"
        onPointerDown={e => onPointerDown(e, widget, 'widget')}
      >
        <span className="material-symbols-outlined text-base text-primary">{spec.icon}</span>
        <span className="flex-1 truncate text-xs font-bold text-gray-700">{spec.label}</span>
        <button
          onClick={() => onRemove(widget.id)}
          className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-500"
          title="Remove widget"
        >
          <span className="material-symbols-outlined text-base">close</span>
        </button>
      </div>
      <div className={`min-h-0 flex-1 overflow-y-auto ${spec.bare ? '' : 'p-3'}`}>
        <DesktopWidgetBody kind={widget.kind} data={data} />
      </div>
      {/* Resize grip — same affordance as the floating windows */}
      <div
        className="absolute bottom-0 right-0 h-5 w-5 cursor-se-resize touch-none"
        onPointerDown={e => onPointerDown(e, widget, 'resize')}
      >
        <div className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-br border-b-2 border-r-2 border-gray-400/70" />
      </div>
    </div>
  );
});
WidgetFrame.displayName = 'WidgetFrame';

// ── Small dialog shell ───────────────────────────────────────────────────────

function Dialog({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onPointerDown={onClose}>
      <div
        className={`flex max-h-full w-full ${wide ? 'max-w-2xl' : 'max-w-md'} flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl`}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center border-b border-gray-200 px-4 py-3">
          <h2 className="flex-1 text-sm font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

// ── Surface ──────────────────────────────────────────────────────────────────

type DialogState =
  | null
  | { type: 'wallpaper' }
  | { type: 'widgets' }
  | { type: 'shortcut'; parentId: string | null }
  | { type: 'rename'; iconId: string };

function DesktopSurface({ userId }: { userId?: number }) {
  const [layout, setLayout] = useState<DesktopLayout>(() => loadLayout(userId));
  const [selected, setSelected] = useState<string | null>(null);
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; iconId: string | null } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [drag, setDrag] = useState<DragInfo | null>(null);
  const [ghost, setGhost] = useState<Ghost | null>(null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<Ghost | null>(null);
  const dropFolderRef = useRef<string | null>(null);
  const layoutRef = useRef(layout);
  const movedRef = useRef(false);
  const { openWindow } = useWindowManager();

  // Mirrors read by the pointer handlers — keeps the drag effect subscribed once
  // per gesture instead of re-binding listeners on every pointermove.
  useEffect(() => { ghostRef.current = ghost; }, [ghost]);
  useEffect(() => { dropFolderRef.current = dropFolder; }, [dropFolder]);
  useEffect(() => { layoutRef.current = layout; }, [layout]);
  useEffect(() => { saveLayout(userId, layout); }, [userId, layout]);

  const kindsKey = useMemo(
    () => [...new Set(layout.widgets.map(w => w.kind))].sort().join(','),
    [layout.widgets]
  );
  const data = useDesktopData(kindsKey);

  const wallpaper = WALLPAPERS.find(w => w.id === layout.wallpaper) ?? WALLPAPERS[0];
  const light = !layout.wallpaperUrl && !!wallpaper.light;
  const background = layout.wallpaperUrl
    ? `center / cover no-repeat url("${layout.wallpaperUrl}")`
    : wallpaper.css;

  const rootIcons = layout.icons.filter(i => !i.parentId);

  const openIcon = useCallback((icon: DesktopIcon) => {
    if (icon.kind === 'folder') { setOpenFolder(icon.id); return; }
    if (!icon.route) return;
    openWindow({ route: icon.route, title: icon.label, icon: icon.icon });
  }, [openWindow]);

  const openIconMenu = useCallback((e: React.MouseEvent, ic: DesktopIcon) => {
    e.preventDefault();
    const r = surfaceRef.current!.getBoundingClientRect();
    setMenu({ x: e.clientX - r.left, y: e.clientY - r.top, iconId: ic.id });
  }, []);

  // ── drag ───────────────────────────────────────────────────────────────────

  const startIconDrag = useCallback((e: React.PointerEvent, icon: DesktopIcon) => {
    e.stopPropagation();
    setMenu(null);
    setSelected(icon.id);
    movedRef.current = false;
    setDrag({
      kind: 'icon', id: icon.id,
      startX: e.clientX, startY: e.clientY,
      origX: icon.x, origY: icon.y, origW: 0, origH: 0,
      touch: e.pointerType === 'touch',
    });
  }, []);

  const startWidgetDrag = useCallback((e: React.PointerEvent, w: DesktopWidget, kind: 'widget' | 'resize') => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.stopPropagation();
    e.preventDefault();
    setMenu(null);
    movedRef.current = false;
    setDrag({
      kind: kind === 'resize' ? 'resize' : 'widget', id: w.id,
      startX: e.clientX, startY: e.clientY,
      origX: w.x, origY: w.y, origW: w.w, origH: w.h,
      touch: e.pointerType === 'touch',
    });
  }, []);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!movedRef.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      movedRef.current = true;
      if (drag.kind === 'resize') {
        setGhost({ id: drag.id, x: drag.origX, y: drag.origY, w: Math.max(MIN_W, drag.origW + dx), h: Math.max(MIN_H, drag.origH + dy) });
      } else {
        const x = snap(drag.origX + dx);
        const y = snap(drag.origY + dy);
        setGhost({ id: drag.id, x, y, w: drag.origW, h: drag.origH });
        if (drag.kind === 'icon') {
          const over = layoutRef.current.icons.find(f =>
            f.kind === 'folder' && !f.parentId && f.id !== drag.id &&
            x + TILE / 2 > f.x && x + TILE / 2 < f.x + TILE &&
            y + 40 > f.y && y + 40 < f.y + TILE);
          setDropFolder(over?.id ?? null);
        }
      }
    };
    const onUp = () => {
      const g = ghostRef.current;
      if (movedRef.current && g) {
        const targetFolder = dropFolderRef.current;
        setLayout(prev => {
          if (drag.kind === 'icon') {
            return {
              ...prev,
              icons: prev.icons.map(i =>
                i.id === drag.id
                  ? targetFolder ? { ...i, parentId: targetFolder } : { ...i, x: g.x, y: g.y }
                  : i),
            };
          }
          return {
            ...prev,
            widgets: prev.widgets.map(w =>
              w.id === drag.id
                ? drag.kind === 'resize' ? { ...w, w: g.w, h: g.h } : { ...w, x: g.x, y: g.y }
                : w),
          };
        });
      } else if (!movedRef.current && drag.kind === 'icon' && drag.touch) {
        // Touch has no double-click — a tap opens, matching phone/tablet habits.
        const icon = layoutRef.current.icons.find(i => i.id === drag.id);
        if (icon) openIcon(icon);
      }
      setDrag(null);
      setGhost(null);
      setDropFolder(null);
      movedRef.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, openIcon]);

  // ── mutations ──────────────────────────────────────────────────────────────

  const surfaceH = surfaceRef.current?.clientHeight ?? 700;

  const addFolder = (name = 'New Folder') => {
    const slot = nextFreeSlot(layout.icons, surfaceH);
    setLayout(p => ({ ...p, icons: [...p.icons, { id: uid(), kind: 'folder', label: name, icon: 'folder', parentId: null, ...slot }] }));
  };

  const addShortcut = (label: string, icon: string, route: string, parentId: string | null) => {
    const slot = nextFreeSlot(layout.icons, surfaceH);
    setLayout(p => ({ ...p, icons: [...p.icons, { id: uid(), kind: 'shortcut', label, icon, route, parentId, ...slot }] }));
  };

  const renameIcon = (id: string, label: string) =>
    setLayout(p => ({ ...p, icons: p.icons.map(i => (i.id === id ? { ...i, label } : i)) }));

  // Deleting a folder frees its contents back onto the wallpaper rather than
  // silently destroying shortcuts the user made.
  const deleteIcon = (id: string) =>
    setLayout(p => ({
      ...p,
      icons: p.icons.filter(i => i.id !== id).map(i => (i.parentId === id ? { ...i, parentId: null } : i)),
    }));

  const moveOutOfFolder = (id: string) => {
    const slot = nextFreeSlot(layout.icons, surfaceH);
    setLayout(p => ({ ...p, icons: p.icons.map(i => (i.id === id ? { ...i, parentId: null, ...slot } : i)) }));
  };

  const addWidget = (kind: WidgetKind) => {
    const spec = widgetSpec(kind);
    setLayout(p => ({
      ...p,
      widgets: [...p.widgets, { id: uid(), kind, x: 200 + (p.widgets.length % 4) * 32, y: 60 + (p.widgets.length % 4) * 32, w: spec.w, h: spec.h }],
    }));
  };

  const removeWidget = useCallback((id: string) =>
    setLayout(p => ({ ...p, widgets: p.widgets.filter(w => w.id !== id) })), []);

  const menuIcon = menu?.iconId ? layout.icons.find(i => i.id === menu.iconId) : null;
  const folder = openFolder ? layout.icons.find(i => i.id === openFolder) : null;
  const folderChildren = folder ? layout.icons.filter(i => i.parentId === folder.id) : [];

  const toolbarBtn =
    'flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-xs font-semibold text-white backdrop-blur-md hover:bg-black/45';

  return (
    <div
      ref={surfaceRef}
      className="relative min-h-0 flex-1 overflow-hidden"
      style={{ background }}
      onPointerDown={e => { if (e.target === e.currentTarget) { setSelected(null); setMenu(null); } }}
      onContextMenu={e => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        const r = surfaceRef.current!.getBoundingClientRect();
        setMenu({ x: e.clientX - r.left, y: e.clientY - r.top, iconId: null });
      }}
    >
      {/* Widgets */}
      {layout.widgets.map(w => {
        const g = ghost && ghost.id === w.id ? ghost : null;
        return (
          <WidgetFrame
            key={w.id}
            widget={w}
            x={g ? g.x : w.x}
            y={g ? g.y : w.y}
            w={g ? g.w : w.w}
            h={g ? g.h : w.h}
            data={data}
            onPointerDown={startWidgetDrag}
            onRemove={removeWidget}
          />
        );
      })}

      {/* Icons */}
      {rootIcons.map(i => {
        const g = ghost && ghost.id === i.id ? ghost : null;
        return (
          <IconTile
            key={i.id}
            icon={i}
            x={g ? g.x : i.x}
            y={g ? g.y : i.y}
            selected={selected === i.id}
            light={light}
            dropTarget={dropFolder === i.id}
            onPointerDown={startIconDrag}
            onOpen={openIcon}
            onMenu={openIconMenu}
          />
        );
      })}

      {/* Toolbar — right-click works too, but a visible bar is discoverable */}
      <div className="absolute right-3 top-3 z-20 flex flex-wrap items-center justify-end gap-1.5">
        <button className={toolbarBtn} onClick={() => setDialog({ type: 'widgets' })}>
          <span className="material-symbols-outlined text-base">widgets</span>Widgets
        </button>
        <button className={toolbarBtn} onClick={() => setDialog({ type: 'shortcut', parentId: null })}>
          <span className="material-symbols-outlined text-base">add_link</span>Shortcut
        </button>
        <button className={toolbarBtn} onClick={() => addFolder()}>
          <span className="material-symbols-outlined text-base">create_new_folder</span>Folder
        </button>
        <button className={toolbarBtn} onClick={() => setDialog({ type: 'wallpaper' })}>
          <span className="material-symbols-outlined text-base">wallpaper</span>Wallpaper
        </button>
        <button
          className={toolbarBtn}
          onClick={() => setDesktopModeEnabled(false)}
          title="Turn Desktop Mode off and return to the classic layout"
        >
          <span className="material-symbols-outlined text-base">logout</span>Exit desktop
        </button>
      </div>

      {/* Context menu */}
      {menu && (
        <div
          className="absolute z-30 min-w-[190px] overflow-hidden rounded-xl border border-white/10 bg-[#1e1e2e]/95 py-1 shadow-2xl backdrop-blur-2xl"
          style={{ left: Math.min(menu.x, (surfaceRef.current?.clientWidth ?? 1000) - 200), top: menu.y }}
          onPointerDown={e => e.stopPropagation()}
        >
          {menuIcon ? (
            <>
              <MenuRow icon="open_in_new" label="Open" onClick={() => { openIcon(menuIcon); setMenu(null); }} />
              <MenuRow icon="edit" label="Rename" onClick={() => { setDialog({ type: 'rename', iconId: menuIcon.id }); setMenu(null); }} />
              {menuIcon.kind === 'folder' && (
                <MenuRow icon="add_link" label="Add shortcut here" onClick={() => { setDialog({ type: 'shortcut', parentId: menuIcon.id }); setMenu(null); }} />
              )}
              <MenuRow icon="delete" label="Delete" danger onClick={() => { deleteIcon(menuIcon.id); setMenu(null); }} />
            </>
          ) : (
            <>
              <MenuRow icon="create_new_folder" label="New folder" onClick={() => { addFolder(); setMenu(null); }} />
              <MenuRow icon="add_link" label="New shortcut…" onClick={() => { setDialog({ type: 'shortcut', parentId: null }); setMenu(null); }} />
              <MenuRow icon="widgets" label="Widgets…" onClick={() => { setDialog({ type: 'widgets' }); setMenu(null); }} />
              <MenuRow icon="wallpaper" label="Change wallpaper…" onClick={() => { setDialog({ type: 'wallpaper' }); setMenu(null); }} />
              <MenuRow icon="restart_alt" label="Reset desktop" danger onClick={() => { setLayout(defaultLayout()); setMenu(null); }} />
            </>
          )}
        </div>
      )}

      {/* Folder contents */}
      {folder && (
        <div className="absolute left-1/2 top-1/2 z-30 w-[540px] max-w-[92%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-2xl">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
            <span className="material-symbols-outlined text-lg text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>folder</span>
            <span className="flex-1 truncate text-sm font-bold text-gray-800">{folder.label}</span>
            <button onClick={() => setDialog({ type: 'shortcut', parentId: folder.id })} className="rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10">Add shortcut</button>
            <button onClick={() => setOpenFolder(null)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200">
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
          <div className="grid max-h-[50vh] grid-cols-4 gap-2 overflow-y-auto p-4">
            {folderChildren.length === 0 && (
              <p className="col-span-4 py-8 text-center text-sm text-gray-400">
                Empty — drag a desktop icon onto the folder, or use “Add shortcut”.
              </p>
            )}
            {folderChildren.map(c => (
              <div key={c.id} className="group flex flex-col items-center gap-1 rounded-lg p-2 hover:bg-gray-50">
                <button onClick={() => openIcon(c)} className="flex flex-col items-center gap-1" title={c.route}>
                  <span className="material-symbols-outlined flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-[24px] text-primary">{c.icon}</span>
                  <span className="w-full break-words text-center text-[11px] font-semibold text-gray-700">{c.label}</span>
                </button>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                  <button onClick={() => moveOutOfFolder(c.id)} title="Move to desktop" className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-primary">
                    <span className="material-symbols-outlined text-sm">output</span>
                  </button>
                  <button onClick={() => deleteIcon(c.id)} title="Delete" className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-red-500">
                    <span className="material-symbols-outlined text-sm">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialogs */}
      {dialog?.type === 'wallpaper' && (
        <WallpaperDialog
          layout={layout}
          onPick={(id) => setLayout(p => ({ ...p, wallpaper: id, wallpaperUrl: '' }))}
          onUrl={(url) => setLayout(p => ({ ...p, wallpaperUrl: url }))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'widgets' && (
        <WidgetDialog
          widgets={layout.widgets}
          onAdd={addWidget}
          onRemove={removeWidget}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'shortcut' && (
        <ShortcutDialog
          parentId={dialog.parentId}
          onAdd={(label, icon, route) => { addShortcut(label, icon, route, dialog.parentId); setDialog(null); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'rename' && menuIconById(layout, dialog.iconId) && (
        <RenameDialog
          initial={menuIconById(layout, dialog.iconId)!.label}
          onSave={(name) => { renameIcon(dialog.iconId, name); setDialog(null); }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

const menuIconById = (l: DesktopLayout, id: string) => l.icons.find(i => i.id === id);

function MenuRow({ icon, label, onClick, danger }: { icon: string; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm ${danger ? 'text-red-400 hover:bg-red-500/10' : 'text-gray-200 hover:bg-white/10'}`}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
      {label}
    </button>
  );
}

// ── Dialog bodies ────────────────────────────────────────────────────────────

function WallpaperDialog({ layout, onPick, onUrl, onClose }: {
  layout: DesktopLayout; onPick: (id: string) => void; onUrl: (url: string) => void; onClose: () => void;
}) {
  const [url, setUrl] = useState(layout.wallpaperUrl);
  return (
    <Dialog title="Wallpaper" onClose={onClose}>
      <div className="grid grid-cols-3 gap-3">
        {WALLPAPERS.map(w => (
          <button
            key={w.id}
            onClick={() => { onPick(w.id); setUrl(''); }}
            className={`overflow-hidden rounded-lg border-2 text-left ${
              !layout.wallpaperUrl && layout.wallpaper === w.id ? 'border-primary' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="h-16 w-full" style={{ background: w.css }} />
            <span className="block px-2 py-1.5 text-xs font-semibold text-gray-700">{w.label}</span>
          </button>
        ))}
      </div>
      <div className="mt-5">
        <label className="mb-1 block text-sm font-medium text-gray-900">Custom image URL</label>
        <div className="flex gap-2">
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://…/photo.jpg"
            className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button onClick={() => onUrl(url.trim())} className="rounded-lg bg-primary px-3 py-2 text-sm font-bold text-white hover:bg-primary/90">Use</button>
        </div>
        <p className="mt-1 text-xs text-gray-500">Leave empty and pick a gradient above to go back to a built-in wallpaper.</p>
      </div>
    </Dialog>
  );
}

function WidgetDialog({ widgets, onAdd, onRemove, onClose }: {
  widgets: DesktopWidget[]; onAdd: (k: WidgetKind) => void; onRemove: (id: string) => void; onClose: () => void;
}) {
  return (
    <Dialog title="Desktop widgets" onClose={onClose}>
      <div className="flex flex-col gap-1.5">
        {WIDGET_CATALOG.map(spec => {
          const placed = widgets.filter(w => w.kind === spec.kind);
          return (
            <div key={spec.kind} className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
              <span className="material-symbols-outlined text-lg text-primary">{spec.icon}</span>
              <span className="flex-1 text-sm font-medium text-gray-800">{spec.label}</span>
              {placed.length > 0 && (
                <button onClick={() => onRemove(placed[placed.length - 1].id)} className="rounded-lg px-2 py-1 text-xs font-semibold text-red-500 hover:bg-red-50">
                  Remove
                </button>
              )}
              <button onClick={() => onAdd(spec.kind)} className="rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/20">
                Add{placed.length > 0 ? ` (${placed.length})` : ''}
              </button>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

function ShortcutDialog({ parentId, onAdd, onClose }: {
  parentId: string | null; onAdd: (label: string, icon: string, route: string) => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customRoute, setCustomRoute] = useState('');
  const items = APP_MENU_GROUPS.flatMap(g => g.items.filter(i => !i.external));
  const filtered = items.filter(i => i.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog title={parentId ? 'Add shortcut to folder' : 'New shortcut'} onClose={onClose} wide>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search pages…"
        className="mb-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {filtered.map(i => (
          <button
            key={i.href}
            onClick={() => onAdd(i.label, i.icon, i.href)}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-2 text-left hover:border-primary/50 hover:bg-primary/5"
          >
            <span className="material-symbols-outlined text-lg text-primary">{i.icon}</span>
            <span className="truncate text-sm font-medium text-gray-800">{i.label}</span>
          </button>
        ))}
      </div>
      <div className="mt-5 border-t border-gray-200 pt-4">
        <p className="mb-2 text-sm font-bold text-gray-900">…or any route</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="Name"
            className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          <input value={customRoute} onChange={e => setCustomRoute(e.target.value)} placeholder="/jobs/12"
            className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          <button
            disabled={!customLabel.trim() || !customRoute.trim().startsWith('/')}
            onClick={() => onAdd(customLabel.trim(), 'link', customRoute.trim())}
            className="rounded-lg bg-primary px-3 py-2 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function RenameDialog({ initial, onSave, onClose }: { initial: string; onSave: (v: string) => void; onClose: () => void }) {
  const [value, setValue] = useState(initial);
  return (
    <Dialog title="Rename" onClose={onClose}>
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && value.trim()) onSave(value.trim()); }}
        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
        <button
          disabled={!value.trim()}
          onClick={() => onSave(value.trim())}
          className="rounded-lg bg-primary px-3 py-2 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-40"
        >
          Save
        </button>
      </div>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export const DesktopPage = () => {
  const desktopMode = useDesktopMode();
  const windowed = useWindowed();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  // A desktop rendered inside one of its own windows would be recursive.
  useEffect(() => { if (windowed) navigate('/dashboard', { replace: true }); }, [windowed, navigate]);

  // Kill switch / small screens: fall straight back to the classic dashboard.
  if (!desktopMode) return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <Taskbar onSearchOpen={() => setSearchOpen(true)} />
      <DesktopSurface userId={user?.id} key={user?.id ?? 'anon'} />
      <FloatingRequestButton />
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </div>
  );
};
